import { useMemo } from 'react';

import { Pill, PillVariant } from '@/components/common/Pill';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  CallabilityInfo,
  getCallabilityInfo,
  TimezoneAbbr,
} from '@/utils/timezone';

/**
 * Maps timezone abbreviations to Pill color variants.
 * EST/CST are gray (common, baseline). PST and beyond are orange (attention).
 */
const TZ_VARIANT: Record<TimezoneAbbr, PillVariant> = {
  EST: 'gray',
  CST: 'gray',
  MST: 'orange',
  PST: 'orange',
  AKST: 'orange',
  HST: 'orange',
};

interface TimezonePillProps {
  /** Two-letter US state abbreviation from the intake (e.g. "WA") */
  stateAbbrev: string | undefined | null;
}

/**
 * Displays a timezone pill for an intake row.
 *
 * - Shows the TZ abbreviation (EST, CST, PST, etc.) as a colored pill
 * - Tooltip shows the PNC's current local time and state
 */
export const TimezonePill = ({ stateAbbrev }: TimezonePillProps) => {
  const info: CallabilityInfo | null = useMemo(
    () => getCallabilityInfo(stateAbbrev),
    // Re-evaluate every render since time changes — the parent table
    // re-renders on data refetch which is sufficient granularity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stateAbbrev],
  );

  if (!info) {
    return <span className="text-gray-500">&mdash;</span>;
  }

  const variant = TZ_VARIANT[info.tzAbbrev] ?? 'gray';

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span className="cursor-help">
          <Pill variant={variant} size="sm">
            {info.tzAbbrev}
          </Pill>
        </span>
      </TooltipTrigger>
      <TooltipContent side="bottom" className="max-w-xs">
        <p className="text-xs">
          Currently {info.localTime} in {info.stateName}
        </p>
      </TooltipContent>
    </Tooltip>
  );
};
